'use client';

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, CheckCircle, MapPin, Video, Calendar, Clock, Stethoscope, ChevronRight, Building2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { listDoctors, getDoctorById, getDoctorAvatarSrc, type DoctorDTO } from '@/services/doctors';
import { createAppointment, getAvailableSlots, rescheduleAppointment, getAppointmentById, type AvailabilityType, type CreateAppointmentDTO } from '@/services/appointments';
import { getPatientByUserId } from '@/services/patients';
import { createPreference, PAYMENT_PRICING, prepareMercadoPagoWindow, launchMercadoPagoCheckout } from '@/services/payments';
import { formatBRL } from '@/lib/currency';
import { resolveServiceTypeFromAppointment } from '@/lib/appointmentServiceType';
import { toast } from 'sonner';

type ServiceType = 'video' | 'home' | 'clinic' | null;
type BookingStep = 'doctor' | 'service' | 'datetime';

interface BookingSummary {
  doctor: DoctorDTO | null;
  serviceType: ServiceType;
  date: string | null;
  time: string | null;
  consultationFee: number;
  serviceFee: number;
}

const SERVICE_FEES: Record<Exclude<ServiceType, null>, number> = {
  video: 0,
  home: 0,
  clinic: 0,
};

const SERVICE_LABELS: Record<Exclude<ServiceType, null>, string> = {
  video: 'Telemedicina',
  home: 'Domiciliar',
  clinic: 'Consultório',
};

const SERVICE_TO_AVAILABILITY: Record<Exclude<ServiceType, null>, AvailabilityType> = {
  video: 'TELEMEDICINE',
  home: 'HOME',
  clinic: 'CLINIC',
};

const APPOINTMENT_TYPE_BY_SERVICE: Record<Exclude<ServiceType, null>, 'TELEMEDICINA' | 'DOMICILIAR' | 'CONSULTORIO'> = {
  video: 'TELEMEDICINA',
  home: 'DOMICILIAR',
  clinic: 'CONSULTORIO',
};

export default function AgendamentoPage() {
  const { doctorId } = useParams<{ doctorId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Modo Reagendamento
  const queryParams = new URLSearchParams(location.search);
  const rescheduleId = queryParams.get('reschedule');
  const isRescheduling = !!rescheduleId;

  const [doctors, setDoctors] = useState<DoctorDTO[]>([]);
  const [filteredDoctors, setFilteredDoctors] = useState<DoctorDTO[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);

  const [currentStep, setCurrentStep] = useState<BookingStep>('doctor');
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<{ time: string; type: AvailabilityType }[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  // Se estiver reagendando, carregar dados da consulta original
  useEffect(() => {
    if (isRescheduling) {
      const loadOriginalAppointment = async () => {
        try {
          const res = await getAppointmentById(rescheduleId!);
          if (res.success && res.data) {
            setSelectedDoctorId(res.data.doctorId);
            const service = resolveServiceTypeFromAppointment(res.data);
            setSelectedServiceType(service);
            // Com modalidade conhecida → datetime; senão deixa escolher serviço (ex.: domiciliar legado)
            setCurrentStep(service ? 'datetime' : 'service');
            toast.info(
              service
                ? 'Modo Remarcação: Selecione uma nova data e horário.'
                : 'Modo Remarcação: Confirme a modalidade e selecione data/horário.',
            );
          }
        } catch (err) {
          console.error('Error loading appointment for reschedule:', err);
          toast.error('Erro ao carregar consulta original');
        }
      };
      loadOriginalAppointment();
    }
  }, [isRescheduling, rescheduleId]);

  useEffect(() => {
    if (doctorId && !isRescheduling) {
      const match = doctors.find((d) => d.id === doctorId || d.userId === doctorId);
      setSelectedDoctorId(match ? match.id : doctorId);
      setCurrentStep('service');
    }
  }, [doctorId, doctors, isRescheduling]);

  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Carrega horários disponíveis
  useEffect(() => {
    const match = doctors.find((d) => d.id === selectedDoctorId || d.userId === selectedDoctorId);
    const targetDoctorId = match?.id || selectedDoctorId;
    if (targetDoctorId && selectedDate && selectedServiceType) {
      const fetchSlots = async () => {
        try {
          setIsLoadingSlots(true);
          const res = await getAvailableSlots(targetDoctorId, selectedDate, SERVICE_TO_AVAILABILITY[selectedServiceType]);
          if (res.success && res.data) {
            setAvailableSlots(res.data);
          }
        } catch (err) {
          console.error('Error loading slots:', err);
          toast.error('Erro ao carregar horários disponíveis');
        } finally {
          setIsLoadingSlots(false);
        }
      };
      fetchSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [doctors, selectedDoctorId, selectedDate, selectedServiceType]);

  // Carrega lista de médicos
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const response = await listDoctors(undefined, undefined, undefined, true);
        if (response.success && response.data) {
          setDoctors(response.data);
          setFilteredDoctors(response.data);
          
          if (doctorId) {
            const match = response.data.find(
              (d) => d.id === doctorId || d.userId === doctorId
            );
            if (match) {
              setSelectedDoctorId(match.id);
            }
          }
        }
      } catch (error) {
        toast.error('Erro ao carregar médicos');
        console.error('Error loading doctors:', error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    loadDoctors();
  }, [doctorId]);

  // Filtra médicos baseado na busca
  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = doctors.filter(
      (doctor) =>
        doctor.user?.name?.toLowerCase().includes(query) ||
        doctor.specialty?.toLowerCase().includes(query)
    );
    setFilteredDoctors(filtered);
  }, [searchQuery, doctors]);

  // Filtra slots baseado no tipo de serviço selecionado
  const filteredSlots = useMemo(() => {
    if (!selectedServiceType) return [];
    return availableSlots.filter(slot => slot.type === SERVICE_TO_AVAILABILITY[selectedServiceType]);
  }, [availableSlots, selectedServiceType]);

  useEffect(() => {
    if (selectedTime && !filteredSlots.some(s => s.time === selectedTime)) {
      setSelectedTime(null);
    }
  }, [filteredSlots, selectedTime]);

  const selectedDoctor = doctors.find((d) => d.id === selectedDoctorId || d.userId === selectedDoctorId);
  const availableServiceTypes = useMemo(() => {
    if (!selectedDoctor) return [];
    const configuredTypes = new Set((selectedDoctor.availability || []).map((block) => block.type || 'CLINIC'));
    return (['video', 'home', 'clinic'] as Array<Exclude<ServiceType, null>>).filter((serviceType) => {
      if (serviceType === 'clinic' && !(selectedDoctor.hasClinic && (selectedDoctor.clinicAddress || selectedDoctor.officeStreet))) {
        return false;
      }
      return configuredTypes.has(SERVICE_TO_AVAILABILITY[serviceType]);
    });
  }, [selectedDoctor]);

  useEffect(() => {
    // Race: enquanto a lista de médicos não carrega, availableServiceTypes=[] —
    // não zerar a modalidade da remarcação (isso escondia data/hora no domiciliar).
    if (!selectedDoctor || availableServiceTypes.length === 0) {
      return;
    }
    if (selectedServiceType && !availableServiceTypes.includes(selectedServiceType)) {
      setSelectedServiceType(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableSlots([]);
      setCurrentStep('service');
      if (isRescheduling) {
        toast.warning(
          'A modalidade original não está disponível na agenda deste médico. Escolha outra modalidade com horários configurados.',
        );
      }
    }
  }, [availableServiceTypes, selectedServiceType, selectedDoctor, isRescheduling]);

  const consultationFee =
    selectedServiceType === 'video'
      ? PAYMENT_PRICING.APPOINTMENT_TELEMEDICINE_CENTS / 100
      : 1;
  const serviceFee = 0;
  const total = consultationFee + serviceFee;

  const canProceedToService = selectedDoctorId !== null;
  const canProceedToDatetime = canProceedToService && selectedServiceType !== null;

  const handleConfirmAppointment = async () => {
    if (!selectedDoctorId || !selectedServiceType || !selectedDate || !selectedTime) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (isRescheduling) {
      await handleReschedule();
      return;
    }

    await handleStartPayment();
  };

  const handleReschedule = async () => {
    if (!rescheduleId || !selectedDate || !selectedTime) return;

    try {
      setIsSubmitting(true);
      const response = await rescheduleAppointment(rescheduleId, {
        date: selectedDate,
        time: selectedTime,
      });

      if (response.success) {
        toast.success('✅ Solicitação de reagendamento enviada com sucesso!');
        setTimeout(() => navigate('/minhas-consultas'), 1500);
      } else {
        toast.error(`❌ Erro ao reagendar: ${response.error || 'Tente novamente'}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error(`❌ Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setIsSubmitting(false);
    }
  };

  const handleStartPayment = async () => {
    if (!selectedDoctorId || !selectedServiceType || !selectedDate || !selectedTime) {
      return;
    }

    const checkoutWindow = prepareMercadoPagoWindow();

    try {
      setIsLoadingPayment(true);

      const doctorResponse = await getDoctorById(selectedDoctor?.id || selectedDoctorId);
      if (!doctorResponse.success || !doctorResponse.data?.userId) {
        checkoutWindow?.close();
        toast.error('Dados do médico não encontrados.');
        setIsLoadingPayment(false);
        return;
      }

      const prefResponse = await createPreference({
        type: 'APPOINTMENT_BOOKING',
        doctorId: doctorResponse.data.userId,
        serviceType: selectedServiceType,
        appointmentDate: selectedDate,
        appointmentTime: selectedTime,
      });

      if (!prefResponse.success || !prefResponse.data) {
        checkoutWindow?.close();
        toast.error(prefResponse.error || 'Erro ao iniciar pagamento');
        setIsLoadingPayment(false);
        return;
      }

      localStorage.setItem(
        'pending_appointment',
        JSON.stringify({
          paymentId: prefResponse.data.paymentId,
          doctorId: selectedDoctorId,
          serviceType: selectedServiceType,
          appointmentDate: selectedDate,
          appointmentTime: selectedTime,
        }),
      );

      launchMercadoPagoCheckout({
        checkoutWindow,
        initPoint: prefResponse.data.initPoint,
        paymentId: prefResponse.data.paymentId,
        goToResult: (path) => navigate(path),
      });
    } catch (error) {
      checkoutWindow?.close();
      toast.error(
        `Erro ao iniciar pagamento: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
      );
      setIsLoadingPayment(false);
    }
  };

  const finalizeAppointment = async (confirmedPaymentId: string) => {
    if (!selectedDoctorId || !selectedServiceType || !selectedDate || !selectedTime) {
      return;
    }

    try {
      const userDataStr = localStorage.getItem('user');
      let userId: string | undefined;

      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          userId = userData.id;
        } catch (e) {
          console.error('[Booking] ✗ Erro ao parsear dados do usuário:', e);
        }
      }

      if (!userId) {
        toast.error('Erro: ID do usuário não encontrado. Faça login novamente.');
        setIsSubmitting(false);
        return;
      }

      const patientResponse = await getPatientByUserId(userId);
      if (!patientResponse.success || !patientResponse.data) {
        toast.error('Dados do paciente não encontrados.');
        setIsSubmitting(false);
        return;
      }

      const patientId = patientResponse.data.userId;
      const doctorResponse = await getDoctorById(selectedDoctor?.id || selectedDoctorId || '');

      if (!doctorResponse.success || !doctorResponse.data) {
        toast.error('Dados do médico não encontrados.');
        setIsSubmitting(false);
        return;
      }

      const appointmentData: CreateAppointmentDTO = {
        doctorId: doctorResponse.data.userId,
        patientId,
        date: selectedDate,
        time: selectedTime,
        type: selectedServiceType as 'home' | 'video' | 'clinic',
        appointmentType: APPOINTMENT_TYPE_BY_SERVICE[selectedServiceType],
        paymentId: confirmedPaymentId,
      };

      const response = await createAppointment(appointmentData);

      if (response.success) {
        toast.success('✅ Pagamento confirmado e agendamento realizado!');
        setTimeout(() => navigate('/minhas-consultas'), 1500);
      } else {
        toast.error(`❌ Erro ao agendar: ${response.error || 'Tente novamente'}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error(`❌ Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-50 to-gray-100 dark:from-neutral-950 dark:to-neutral-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                {isRescheduling ? 'Remarcar Consulta' : 'Agendar uma Consulta'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isRescheduling 
                  ? 'Escolha um novo horário para sua consulta existente.' 
                  : 'Agende uma consulta com nossos especialistas em três passos simples.'}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/buscar-medicos')}>
              Voltar para a Busca
            </Button>
          </div>
        </div>

        {isRescheduling && (
          <div className="mb-8 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 flex items-start gap-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-amber-900 dark:text-amber-200">Modo de Remarcação Ativo</h2>
              <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                Você está alterando o horário da sua consulta com <strong>{selectedDoctor?.user?.name || 'médico'}</strong>. 
                Uma nova solicitação será enviada para o médico.
              </p>
              <button 
                onClick={() => navigate('/agendamento')}
                className="mt-3 text-xs font-bold text-amber-700 dark:text-amber-400 underline hover:no-underline"
              >
                Cancelar remarcação e agendar nova consulta
              </button>
            </div>
          </div>
        )}

        {/* Immediate Care Banner - Only show if not rescheduling */}
        {!isRescheduling && (
          <div className="mb-8 bg-linear-to-r from-teal-600 to-teal-800 dark:from-teal-800 dark:to-teal-950 rounded-2xl p-8 text-white shadow-lg shadow-teal-500/20 relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="max-w-md">
                <h2 className="text-3xl font-bold mb-2">Precisa de atendimento agora?</h2>
                <p className="text-teal-50/80 text-sm">
                  Conecte-se com um médico de plantão em poucos minutos para consultas de baixa complexidade.
                </p>
              </div>
              <button
                onClick={() => navigate('/on-demand/triage')}
                className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group whitespace-nowrap"
              >
                <Stethoscope className="w-6 h-6" />
                Falar com um médico agora
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-400/10 rounded-full -ml-10 -mb-10 blur-2xl" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Step 1: Select Doctor */}
            {doctorId ? (
              // Preselected doctor view: Only show selected doctor details, no search bar, no dropdown/grid, no swapping
              <div className="transition-opacity duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-600 text-white font-bold">
                    1
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Médico Selecionado
                  </h2>
                </div>

                {loadingDoctors ? (
                  <Card className="h-32 bg-gray-100 dark:bg-neutral-800 animate-pulse max-w-md" />
                ) : selectedDoctor ? (
                  <Card className="p-4 border-2 border-teal-600 bg-teal-50 dark:bg-teal-900/20 shadow-md max-w-md">
                    <div className="flex gap-4">
                      {/* Avatar */}
                      <div className="shrink-0">
                        {getDoctorAvatarSrc(selectedDoctor) ? (
                          <img
                            src={getDoctorAvatarSrc(selectedDoctor)!}
                            alt={selectedDoctor.user?.name || 'Médico'}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-linear-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                            {selectedDoctor.user?.name
                              ?.split(' ')
                              .slice(0, 2)
                              .map((n) => n[0])
                              .join('')
                              .toUpperCase() || 'DR'}
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white truncate">
                              Dr(a). {selectedDoctor.user?.name || 'Médico'}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                              {selectedDoctor.specialty}
                            </p>
                          </div>
                          <CheckCircle className="w-5 h-5 text-teal-600 shrink-0" />
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            4.9 ⭐
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            (348 avaliações)
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <p className="text-red-500 text-sm">Médico não encontrado</p>
                )}
              </div>
            ) : (
              // Default doctor list view (fallback flow when doctorId is not in URL)
              <div className={cn(
                'transition-opacity duration-300',
                currentStep !== 'doctor' && 'opacity-50 pointer-events-none'
              )}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-600 text-white font-bold">
                    1
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Escolha Especialidade ou Médico
                  </h2>
                </div>

                {/* Search Bar */}
                <div className="relative mb-6">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Busque por nome, especialidade ou condição..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700"
                  />
                </div>

                {/* Doctors Grid */}
                {loadingDoctors ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2].map((i) => (
                      <Card key={i} className="h-32 bg-gray-100 dark:bg-neutral-800 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredDoctors.slice(0, 6).map((doctor) => (
                      <Card
                        key={doctor.id}
                        className={cn(
                          'p-4 cursor-pointer transition-all duration-200 border-2',
                          selectedDoctorId === doctor.id
                            ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20 shadow-md'
                            : 'border-gray-200 dark:border-neutral-700 hover:border-teal-300 dark:hover:border-teal-700'
                        )}
                        onClick={() => {
                          setSelectedDoctorId(doctor.id);
                          setSelectedServiceType(null);
                          setSelectedDate(null);
                          setSelectedTime(null);
                          setAvailableSlots([]);
                          setCurrentStep('service');
                        }}
                      >
                        <div className="flex gap-4">
                          {/* Avatar */}
                          <div className="shrink-0">
                            {getDoctorAvatarSrc(doctor) ? (
                              <img
                                src={getDoctorAvatarSrc(doctor)!}
                                alt={doctor.user?.name || 'Médico'}
                                className="w-16 h-16 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-linear-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                                {doctor.user?.name
                                  ?.split(' ')
                                  .slice(0, 2)
                                  .map((n) => n[0])
                                  .join('')
                                  .toUpperCase() || 'DR'}
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h3 className="font-bold text-gray-900 dark:text-white truncate">
                                  {doctor.user?.name || 'Médico'}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {doctor.specialty}
                                </p>
                              </div>
                              {selectedDoctorId === doctor.id && (
                                <CheckCircle className="w-5 h-5 text-teal-600 shrink-0" />
                              )}
                            </div>

                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                4.9 ⭐
                              </Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-500">
                                (348 avaliações)
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Service Type */}
            {canProceedToService && (
              <div className={cn(
                'transition-opacity duration-300 animate-in fade-in slide-in-from-bottom-4',
                currentStep !== 'service' && currentStep !== 'datetime' && 'opacity-50 pointer-events-none'
              )}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-600 text-white font-bold">2</div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Escolha o Tipo de Serviço</h2>
                </div>

                {availableServiceTypes.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableServiceTypes.map((serviceType) => {
                      const Icon = serviceType === 'video' ? Video : serviceType === 'home' ? MapPin : Building2;
                      const iconClassName = serviceType === 'video'
                        ? 'text-blue-600 dark:text-blue-400'
                        : serviceType === 'home'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-teal-600 dark:text-teal-400';
                      const iconBgClassName = serviceType === 'video'
                        ? 'bg-blue-100 dark:bg-blue-900/30'
                        : serviceType === 'home'
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : 'bg-teal-100 dark:bg-teal-900/30';
                      const description = serviceType === 'video'
                        ? 'Videochamada via aplicativo'
                        : serviceType === 'home'
                          ? 'O médico irá até a sua residência'
                          : 'Atendimento na clínica';

                      return (
                        <Card
                          key={serviceType}
                          className={cn(
                            'p-6 cursor-pointer transition-all duration-200 border-2',
                            selectedServiceType === serviceType ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20 shadow-md' : 'border-gray-200 dark:border-neutral-700 hover:border-teal-300 dark:hover:border-teal-700'
                          )}
                          onClick={() => {
                            setSelectedServiceType(serviceType);
                            setSelectedDate(null);
                            setSelectedTime(null);
                            setAvailableSlots([]);
                            setCurrentStep('datetime');
                          }}
                        >
                          <div className="flex items-start gap-4">
                            <div className={cn('p-3 rounded-lg', iconBgClassName)}>
                              <Icon className={cn('w-6 h-6', iconClassName)} />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900 dark:text-white mb-1">{SERVICE_LABELS[serviceType]}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{description}</p>
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {serviceType === 'clinic'
                                  ? 'Grátis'
                                  : serviceType === 'video'
                                    ? formatBRL(PAYMENT_PRICING.APPOINTMENT_TELEMEDICINE_CENTS / 100)
                                    : formatBRL(SERVICE_FEES[serviceType])}
                              </p>
                            </div>
                            {selectedServiceType === serviceType && <CheckCircle className="w-5 h-5 text-teal-600 shrink-0 mt-1" />}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
                    Este médico ainda não configurou horários disponíveis para agendamento.
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Select Date and Time */}
            {canProceedToDatetime && (
              <div className="animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-600 text-white font-bold">3</div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Selecione Data e Hora</h2>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    Escolha a Data
                  </h3>
                  <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg">
                    <Input
                      type="date"
                      value={selectedDate || ''}
                      min={new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedTime(null);
                      }}
                      className="w-full text-lg p-4 h-14 bg-transparent border-gray-200 dark:border-neutral-700 dark:text-white"
                    />
                  </div>
                </div>

                {selectedDate && (
                  <div className="animate-in fade-in slide-in-from-top-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-teal-600" />
                      Horários Disponíveis ({new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {isLoadingSlots ? (
                        [1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 dark:bg-neutral-800 animate-pulse rounded-lg" />)
                      ) : filteredSlots.length > 0 ? (
                        filteredSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            className={cn(
                              'py-3 px-4 rounded-lg font-semibold transition-all duration-200',
                              selectedTime === slot.time ? 'bg-teal-600 text-white shadow-md' : 'bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 text-gray-900 dark:text-white hover:border-teal-400 dark:hover:border-teal-600'
                            )}
                            onClick={() => setSelectedTime(slot.time)}
                          >
                            {slot.time}
                          </button>
                        ))
                      ) : (
                        <p className="col-span-full text-center py-8 text-gray-500 italic">
                          Nenhum horário disponível para esta data
                          {selectedServiceType
                            ? ` na modalidade ${SERVICE_LABELS[selectedServiceType]}`
                            : ''}
                          . Tente outra data ou verifique se o médico possui agenda {selectedServiceType === 'home' ? 'domiciliar' : 'para esta modalidade'}.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6 bg-white dark:bg-neutral-800 shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Resumo da {isRescheduling ? 'Remarcação' : 'Reserva'}</h3>
              
              {selectedDoctor ? (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-neutral-900 rounded-lg flex gap-3">
                  <div className="w-12 h-12 rounded-full bg-linear-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {selectedDoctor.user?.name?.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900 dark:text-white truncate">{selectedDoctor.user?.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{selectedDoctor.specialty}</p>
                  </div>
                </div>
              ) : <div className="mb-6 p-4 bg-gray-100 dark:bg-neutral-700 rounded-lg text-gray-400 text-sm">Selecione um médico</div>}

              {selectedServiceType ? (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-neutral-900 rounded-lg">
                  <p className="text-xs uppercase text-gray-500 font-semibold mb-2">Tipo de Serviço</p>
                  <p className="font-bold text-gray-900 dark:text-white">{SERVICE_LABELS[selectedServiceType]}</p>
                </div>
              ) : <div className="mb-6 p-4 bg-gray-100 dark:bg-neutral-700 rounded-lg text-gray-400 text-sm">Selecione o tipo</div>}

              {selectedDate && selectedTime ? (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-neutral-900 rounded-lg">
                  <p className="text-xs uppercase text-gray-500 font-semibold mb-2">Data e Hora</p>
                  <p className="font-bold text-gray-900 dark:text-white">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')} às {selectedTime}</p>
                </div>
              ) : <div className="mb-6 p-4 bg-gray-100 dark:bg-neutral-700 rounded-lg text-gray-400 text-sm">Selecione data e hora</div>}

              <div className="border-t border-gray-200 dark:border-neutral-700 my-6" />
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span className="text-sm">Consulta</span>
                  <span className="font-semibold">{formatBRL(consultationFee)}</span>
                </div>
                {selectedServiceType && (
                  <div className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span className="text-sm">Serviço</span>
                    <span className="font-semibold">{formatBRL(serviceFee)}</span>
                  </div>
                )}
                <div className="pt-4 flex justify-between items-center border-t border-gray-100 dark:border-neutral-800">
                  <span className="font-bold text-gray-900 dark:text-white">Total</span>
                  <span className="text-2xl font-bold text-teal-600">{formatBRL(total)}</span>
                </div>
              </div>

              <Button
                onClick={handleConfirmAppointment}
                disabled={!canProceedToDatetime || !selectedTime || isSubmitting || isLoadingPayment}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 h-auto disabled:opacity-50"
              >
                {isSubmitting || isLoadingPayment
                  ? 'Processando...'
                  : isRescheduling
                    ? 'Confirmar Reagendamento'
                    : 'Pagar com Mercado Pago'}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
